/** The mail search box with Outlook-style suggestions (2026-10-02).
 *
 *  As you type, a list opens under the box: "Emails from Carolyn
 *  McDonald", "Emails sent to Carolyn McDonald", operators, dates, folders
 *  (lib: ../searchSuggest.ts). ↑ ↓ choose, Enter runs the chosen one (or
 *  the box as typed when none is chosen), Tab completes without running,
 *  Esc or a click elsewhere closes. People come from the mail you have
 *  exchanged (GET /api/mail/people), best first. A sentence ("from ann
 *  last week") comes first, understood, and is what Enter runs; one the
 *  patterns miss ("mails my boss sent that I never answered") goes to the
 *  AI, and Enter waits for its answer (a few seconds at most, then the
 *  words as typed). Saved searches lead an empty box. Lives in the title
 *  bar (TitleBar.tsx). */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  ArrowDownLeft,
  AtSign,
  CalendarDays,
  Flag,
  Folder,
  History,
  Paperclip,
  RefreshCw,
  Search as SearchIcon,
  Send,
  SlidersHorizontal,
  Sparkles,
  Star,
  Tag,
  Type,
  Users,
} from "lucide-react";
import { listenForDismiss } from "../../../lib/dismiss";
import { parsePeopleResponse } from "../recipientSuggest";
import { buildSearchSuggestions, defaultSuggestionIndex, peopleQueryFor, readRecentSearches, wantsAiUnderstanding, type SearchSuggestion, type SuggestPerson, type SuggestionIcon } from "../searchSuggest";
import { csrfFetch } from "../../../lib/csrfFetch";
import { interpretNaturalSearch } from "@waki/wov-contract/mail-search";
import { boxMode, type BoxMode } from "../../assistant/ask";

const ICONS: Record<SuggestionIcon, { icon: ReactNode; tint: string }> = {
  smart: { icon: <Sparkles className="h-3.5 w-3.5" />, tint: "text-violet-600 bg-violet-100 dark:text-violet-200 dark:bg-violet-900/60" },
  saved: { icon: <Star className="h-3.5 w-3.5" />, tint: "text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/40" },
  from: { icon: <ArrowDownLeft className="h-4 w-4" />, tint: "text-sky-600 bg-sky-50 dark:text-sky-300 dark:bg-sky-900/40" },
  to: { icon: <Send className="h-3.5 w-3.5" />, tint: "text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/40" },
  with: { icon: <Users className="h-3.5 w-3.5" />, tint: "text-violet-600 bg-violet-50 dark:text-violet-300 dark:bg-violet-900/40" },
  cc: { icon: <AtSign className="h-3.5 w-3.5" />, tint: "text-teal-600 bg-teal-50 dark:text-teal-300 dark:bg-teal-900/40" },
  subject: { icon: <Type className="h-3.5 w-3.5" />, tint: "text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-700/60" },
  search: { icon: <SearchIcon className="h-3.5 w-3.5" />, tint: "text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-700/60" },
  operator: { icon: <SlidersHorizontal className="h-3.5 w-3.5" />, tint: "text-indigo-600 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/40" },
  flag: { icon: <Flag className="h-3.5 w-3.5" />, tint: "text-rose-600 bg-rose-50 dark:text-rose-300 dark:bg-rose-900/40" },
  attachment: { icon: <Paperclip className="h-3.5 w-3.5" />, tint: "text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/40" },
  date: { icon: <CalendarDays className="h-3.5 w-3.5" />, tint: "text-orange-600 bg-orange-50 dark:text-orange-300 dark:bg-orange-900/40" },
  folder: { icon: <Folder className="h-3.5 w-3.5" />, tint: "text-yellow-700 bg-yellow-50 dark:text-yellow-300 dark:bg-yellow-900/40" },
  category: { icon: <Tag className="h-3.5 w-3.5" />, tint: "text-fuchsia-600 bg-fuchsia-50 dark:text-fuchsia-300 dark:bg-fuchsia-900/40" },
  recent: { icon: <History className="h-3.5 w-3.5" />, tint: "text-slate-500 bg-slate-100 dark:text-slate-400 dark:bg-slate-700/60" },
};

type AiReading = { query: string; description: string };

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function SearchBox({
  inputRef,
  value,
  onChange,
  onSubmit,
  refreshing,
  folders,
  categories,
  saved = [],
  size = "md",
  onClear,
  onAsk,
  scope = null,
}: {
  /** The mail folder on screen, offered as "Search <folder> for …". */
  scope?: { path: string; name: string } | null;
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  onChange: (v: string) => void;
  /** Run this query; `said` is the sentence it was understood from. */
  onSubmit: (q: string, said?: string) => void;
  refreshing: boolean;
  folders: string[];
  categories: string[];
  saved?: Array<{ name: string; query: string }>;
  /** "sm": the compact field of the Mac app's title bar. */
  size?: "sm" | "md";
  /** The box was cleared (its ×) or Esc pressed with nothing open: close
   *  the search. */
  onClear?: () => void;
  /** Answer this question (the Ask page). Without it the box only searches. */
  onAsk?: (q: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Ask or search (2026-10-06). A question is asked and anything else
  // searched, unless you pick the other in the box; the pick lasts until
  // the box is emptied.
  const [chosen, setChosen] = useState<BoxMode | null>(null);
  useEffect(() => {
    if (!value.trim()) setChosen(null);
  }, [value]);
  const mode: BoxMode = onAsk && value.trim() ? boxMode(value, chosen) : "search";
  const asking = mode === "ask";
  const [active, setActive] = useState(-1);
  const [people, setPeople] = useState<SuggestPerson[]>([]);
  const [recent, setRecent] = useState<string[]>(() => readRecentSearches());
  useEffect(() => {
    const onRecent = () => setRecent(readRecentSearches());
    window.addEventListener("wov:recent-searches", onRecent);
    return () => window.removeEventListener("wov:recent-searches", onRecent);
  }, []);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // People for the name being typed, a moment after typing stops.
  const peopleQ = open ? peopleQueryFor(value, localToday()) : null;
  useEffect(() => {
    if (!peopleQ) {
      setPeople([]);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const r = await fetch(`/api/mail/people?q=${encodeURIComponent(peopleQ)}&limit=5`, { credentials: "same-origin", signal: ctrl.signal });
        if (!ctrl.signal.aborted) setPeople(parsePeopleResponse(await r.json()).map((p) => ({ name: p.name, address: p.address })));
      } catch {
        /* aborted or offline: no people */
      }
    }, 120);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [peopleQ]);

  // A sentence the patterns did not understand: ask the AI (Settings >
  // AI, "Search understanding"), a moment after typing stops.
  const [ai, setAi] = useState<{ for: string; query: string; description: string } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const aiCache = useRef(new Map<string, AiReading | null>());
  const aiPending = useRef(new Map<string, Promise<AiReading | null>>());
  /** The AI's reading of a sentence: remembered, and asked once however
   *  many times it is wanted (typing, then Enter). */
  const understand = useCallback((text: string): Promise<AiReading | null> => {
    if (aiCache.current.has(text)) return Promise.resolve(aiCache.current.get(text) ?? null);
    const pending = aiPending.current.get(text);
    if (pending) return pending;
    const p = (async () => {
      try {
        const r = await csrfFetch("/api/mail/search/understand", { method: "POST", body: JSON.stringify({ text, today: localToday() }) }, { timeoutMs: 15_000 });
        const j = (await r.json().catch(() => null)) as { ok?: boolean; data?: { query: string | null; description?: string } } | null;
        const hit = j?.ok && j.data?.query ? { query: j.data.query, description: j.data.description ?? j.data.query } : null;
        aiCache.current.set(text, hit);
        return hit;
      } catch {
        return null; // timed out or offline: not remembered, so asked again next time
      } finally {
        aiPending.current.delete(text);
      }
    })();
    aiPending.current.set(text, p);
    return p;
  }, []);
  const wantsAi = open && !asking && wantsAiUnderstanding(value, localToday());
  useEffect(() => {
    if (!wantsAi) {
      setAiBusy(false);
      return;
    }
    const text = value.trim();
    if (aiCache.current.has(text)) {
      const hit = aiCache.current.get(text);
      setAi(hit ? { for: text, ...hit } : null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      setAiBusy(true);
      void understand(text).then((hit) => {
        if (cancelled) return;
        setAi(hit ? { for: text, ...hit } : null);
        setAiBusy(false);
      });
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [wantsAi, value, understand]);
  const aiForValue = ai && ai.for === value.trim() ? ai : null;

  const suggestions = useMemo(
    () => (open ? buildSearchSuggestions(value, { people, saved, recent, folders, categories, today: localToday(), ai: aiForValue, scope }) : []),
    [open, value, people, saved, recent, folders, categories, aiForValue, scope],
  );
  // A sentence the box understood is what Enter runs, and shows as chosen.
  const defaultIdx = defaultSuggestionIndex(suggestions);
  // Asking, nothing is chosen: Enter asks.
  useEffect(() => setActive(asking ? -1 : defaultIdx), [value, defaultIdx, asking]);
  useEffect(() => (open ? listenForDismiss((t) => !!boxRef.current?.contains(t), () => setOpen(false)) : undefined), [open]);
  useEffect(() => {
    if (active < 0) return;
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const ask = (q: string) => {
    const t = q.trim();
    if (!t || !onAsk) return;
    cancelThinking();
    onChange(t);
    onAsk(t);
    setOpen(false);
    inputRef.current?.blur();
  };
  const run = (q: string, said?: string) => {
    const t = q.trim();
    if (!t) return;
    cancelThinking();
    onChange(t);
    onSubmit(t, said && said !== t ? said : undefined);
    setOpen(false);
    setRecent(readRecentSearches());
    inputRef.current?.blur();
  };
  // Enter on a sentence nothing has understood yet: wait for the AI (8 s at
  // most), then run its reading, or the words as typed if it has none.
  // Typing on or Esc stops waiting.
  const [thinking, setThinking] = useState(false);
  const thinkingRun = useRef(0);
  const cancelThinking = () => {
    thinkingRun.current++;
    setThinking(false);
  };
  const runSentence = async (text: string) => {
    const mine = ++thinkingRun.current;
    setThinking(true);
    setOpen(true);
    const hit = await Promise.race([understand(text), new Promise<null>((r) => window.setTimeout(() => r(null), 8_000))]);
    if (mine !== thinkingRun.current) return;
    setThinking(false);
    if ((inputRef.current?.value ?? text).trim() !== text) return;
    // No AI answer: the patterns' partial reading if any (shown as
    // understood, with the way back to the exact words), else the words.
    const fallback = hit ?? interpretNaturalSearch(text, localToday());
    if (fallback) run(fallback.query, text);
    else run(text);
  };
  const pick = (s: SearchSuggestion) => {
    if (s.run) return run(s.query, s.icon === "smart" ? value.trim() : undefined);
    onChange(s.query);
    setOpen(true);
    window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(s.query.length, s.query.length);
    });
  };

  return (
    <div ref={boxRef} className="relative">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (thinking) return;
          if (active >= 0 && suggestions[active]) pick(suggestions[active]);
          else if (asking) ask(value);
          else if (wantsAiUnderstanding(value, localToday())) void runSentence(value.trim());
          else run(value);
        }}
        role="search"
      >
        <SearchIcon className={"pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 " + (size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4")} />
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(e) => {
            if (thinking) cancelThinking();
            // macOS curls typed quotes; search reads straight ones.
            onChange(e.target.value.replace(/[\u201C\u201D\u201E\u201F]/g, '"'));
            setOpen(true);
          }}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onFocus={() => setOpen(true)}
          onInput={(e) => {
            // The field's own × clears it without a key press.
            const ne = e.nativeEvent as InputEvent;
            if ((e.target as HTMLInputElement).value === "" && !ne.inputType && onClear) {
              setOpen(false);
              onClear();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, suggestions.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, -1));
            } else if (e.key === "Tab" && open && suggestions.length > 0 && !e.shiftKey) {
              const s = suggestions[Math.max(active, 0)];
              if (s && s.query !== value) {
                e.preventDefault();
                onChange(s.query + (s.run ? " " : ""));
              }
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              if (thinking) cancelThinking();
              else if (open && suggestions.length > 0) setOpen(false);
              else if (onClear) {
                onClear();
                inputRef.current?.blur();
              }
            }
          }}
          role="combobox"
          aria-expanded={open && suggestions.length > 0}
          aria-controls="wov-search-suggestions"
          aria-activedescendant={active >= 0 ? `wov-search-s-${active}` : undefined}
          aria-autocomplete="list"
          placeholder={size === "sm" ? "Search mail, or ask a question…" : "Search mail, or ask: “when is my next flight?”"}
          className={
            "w-full border border-slate-200 bg-white/80 pl-9 text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-400/30 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-100 dark:focus:bg-slate-800 " +
            (size === "sm" ? "h-[30px] rounded-lg text-[13px]" : "h-9 rounded-xl text-[14px]") +
            (onAsk && value.trim() ? (size === "sm" ? " pr-[124px]" : " pr-[136px]") : " pr-14")
          }
        />
        {onAsk && value.trim() && !thinking && !refreshing && (
          // What Enter will do, and the way to do the other.
          <div role="radiogroup" aria-label="Ask or search" className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center rounded-md bg-slate-100 p-0.5 text-[11px] font-semibold dark:bg-slate-900/70">
            {(["ask", "search"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setChosen(m);
                  setOpen(true);
                  inputRef.current?.focus();
                }}
                title={m === "ask" ? "Answer it from your mail, calendar, tasks and trips" : "Search mail for these words"}
                className={
                  "inline-flex items-center gap-1 rounded px-1.5 " + (size === "sm" ? "py-[1px]" : "py-0.5") + " " +
                  (mode === m
                    ? m === "ask"
                      ? "bg-white text-fuchsia-700 shadow-sm dark:bg-slate-700 dark:text-fuchsia-300"
                      : "bg-white text-sky-700 shadow-sm dark:bg-slate-700 dark:text-sky-300"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200")
                }
              >
                {m === "ask" ? <Sparkles className="h-3 w-3" /> : <SearchIcon className="h-3 w-3" />}
                {m === "ask" ? "Ask" : "Search"}
              </button>
            ))}
          </div>
        )}
        {thinking && (
          <Sparkles aria-label="Understanding your sentence" className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-pulse text-violet-500 motion-reduce:animate-none" />
        )}
        {!value && !refreshing && (
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] font-semibold text-slate-400 dark:border-slate-600 dark:bg-slate-700/60 dark:text-slate-400">⌘K</kbd>
        )}
        {refreshing && !thinking && value.length > 0 && (
          <RefreshCw aria-hidden className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-sky-500 motion-reduce:animate-none" />
        )}
      </form>

      {open && (suggestions.length > 0 || asking) && (
        <div className="wov-fade absolute left-1/2 top-full z-[90] mt-1.5 w-full min-w-[460px] -translate-x-1/2 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          {asking && (
            // Asking: the answer first; the mail searches below are for a
            // change of mind.
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(-1)}
              onClick={() => ask(value)}
              className={"flex w-full items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-left dark:border-slate-700 " + (active < 0 ? "bg-fuchsia-50 dark:bg-fuchsia-950/40" : "")}
            >
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/60 dark:text-fuchsia-200"><Sparkles className="h-3.5 w-3.5" /></span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-slate-600 dark:text-slate-300">
                Ask <span className="font-semibold text-slate-900 dark:text-white">{value.trim()}</span>
              </span>
              <span className="flex-shrink-0 text-[11px] text-slate-400">mail, calendar, tasks, trips · Enter</span>
            </button>
          )}
          {asking && suggestions.length > 0 && (
            <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Or search mail</div>
          )}
          <ul ref={listRef} id="wov-search-suggestions" role="listbox" className="max-h-[360px] overflow-y-auto py-1">
            {suggestions.map((s, i) => {
              const ic = ICONS[s.icon];
              return (
                <li key={s.id} id={`wov-search-s-${i}`} data-idx={i} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(s)}
                    className={"flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors " + (i === active ? "bg-sky-50 dark:bg-sky-900/40" : "")}
                  >
                    <span className={"flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md " + ic.tint}>{ic.icon}</span>
                    <span className="min-w-0 flex-1 basis-auto truncate text-[13px] text-slate-600 dark:text-slate-300" style={{ flexShrink: 0.5 }}>
                      {s.lead && <span>{s.lead} </span>}
                      <span className="font-semibold text-slate-900 dark:text-white">{s.strong}</span>
                    </span>
                    {s.hint && <span className="max-w-[36%] flex-shrink truncate text-[11px] text-slate-400">{s.hint}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {(aiBusy || thinking) && !aiForValue && (
            <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-[11.5px] text-violet-700 dark:border-slate-700 dark:text-violet-300">
              <Sparkles className="h-3.5 w-3.5 animate-pulse" /> Understanding your sentence…
              {thinking && <span className="text-slate-400">it runs as soon as it is understood · Esc to stop</span>}
            </div>
          )}
          <div className="border-t border-slate-100 bg-slate-50/80 px-3 py-1.5 text-[10.5px] text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
            <span className="font-semibold">↑↓</span> choose · <span className="font-semibold">Enter</span> {asking ? "ask" : "search"} · <span className="font-semibold">Tab</span> complete · {onAsk ? "ask a question, or search with" : "ask in a sentence, or"}{" "}
            <span className="font-mono">from: to: cc: with: subject: has:attachment is:unread received:"last week" in:sent</span>
          </div>
        </div>
      )}
    </div>
  );
}
